/**
 * @copyright
 * Copyright (c) 2024 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */

/**
 * @function formatDate
 * Format date
 *
 * @param {string} date
 * @param {DateFormat} format
 * @param {string} locale
 *
 * @returns {string} formatted date.
 *
 * @example
 *   formatDate("2022-05-31 12:20:10", DateFormat.SHORT)
 *   // => 05/31/22
 *
 * @example
 *  formatDate("2022-05-31 12:20:10", DateFormat.SHORT, "hu-HU")
 *  // => 2022. 12. 12.
 *
 * @example
 *   formatDate("this will be invalid", dateFormat.SHORT, "lol")
 *   // => ""
 */
import isNaN from "lodash/fp/isNaN";

export enum DateFormat {
  LONG = "full",
  SHORT = "short",
  MEDIUM = "medium",
}

const formatDate = (date: string, format: DateFormat, locale?: string) => {
  // Cut off the time component, since we don't need it, if there is no time component, this just returns the date
  const dateOnlyString = date.split(" ")[0];
  // Concat midnight time and UTC timezone to the date, to ensure correct parsing in different locales
  const timestamp = Date.parse(`${dateOnlyString}T00:00:00.000Z`);

  return !isNaN(timestamp)
    ? new Intl.DateTimeFormat(locale ?? "en-US", {
        ...(format !== DateFormat.MEDIUM && { dateStyle: format }),
        timeZone: "UTC",
      }).format(timestamp)
    : "";
};

export default formatDate;
